import { useNavigationBarQuery } from "@crowdcoursing/api/graphql";
import { Business as BusinessIcon, OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useFeature } from "~hooks/useFeature";
import { useMyOrganizationCardDetails } from "~hooks/useMyOrganizationCardDetails";
import { useMyOrganizations } from "~hooks/useMyOrganizations";
import { Route } from "~services/router/Route";
import { PageHeader } from "~ui/atoms/PageHeader/PageHeader";
import { SearchField } from "~ui/atoms/SearchField/SearchField";

import { CreateOrganizationDialog } from "../../organisms/Organizations/CreateOrganizationDialog";
import { MyOrganizationsTemplate } from "../../templates/MyOrganizationsTemplate/MyOrganizationsTemplate";

export const tMyOrganizations = "My Organizations";
export const tCreateOrganization = "Create";
export const tViewDirectory = "View Directory";

export const MyOrganizationsPage = () => {
  const [isOrganizationDialogOpen, setIsOrganizationDialogOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { data: navData } = useNavigationBarQuery();
  const isSiteAdmin = navData?.me.isSiteAdmin ?? false;
  const publicPagesEnabled = useFeature("publicPages");

  const { organizations, isLoading, error } = useMyOrganizations();

  const filteredOrganizations = useMemo(() => {
    if (!organizations || !searchValue.trim()) {
      return organizations ?? [];
    }

    const searchTerm = searchValue.toLowerCase().trim();
    return organizations.filter((org) => {
      const orgName = org.name?.toLowerCase() ?? "";
      const orgAddress = org.address?.toLowerCase() ?? "";

      return orgName.includes(searchTerm) || orgAddress.includes(searchTerm);
    });
  }, [organizations, searchValue]);

  const organizationsWithDetails = useMyOrganizationCardDetails(filteredOrganizations);

  const handleOpenOrganizationDialog = () => setIsOrganizationDialogOpen(true);
  const handleCloseOrganizationDialog = () => setIsOrganizationDialogOpen(false);

  return (
    <Box sx={{ p: { sm: 2, xs: 1.5 } }}>
      <Stack spacing={2}>
        <PageHeader
          icon={<BusinessIcon data-variant="page-header" />}
          rightContent={
            <Box sx={{ display: "flex", gap: 1 }}>
              {publicPagesEnabled && (
                <Button
                  component={Link}
                  rel="noopener noreferrer"
                  startIcon={<OpenInNewIcon fontSize="small" />}
                  sx={{
                    "&:hover": { backgroundColor: "action.hover" },
                    backgroundColor: "white",
                    border: 1,
                    borderColor: "primary.main",
                    color: "primary.main",
                  }}
                  target="_blank"
                  to={Route.publicDirectory()}
                  variant="page-header"
                >
                  {tViewDirectory}
                </Button>
              )}
              {isSiteAdmin && (
                <Button onClick={handleOpenOrganizationDialog} variant="page-header">
                  {tCreateOrganization}
                </Button>
              )}
            </Box>
          }
          title={tMyOrganizations}
        />
        <SearchField
          onChange={setSearchValue}
          placeholder="Search organizations..."
          value={searchValue}
          sx={{ mb: 1.5 }}
        />
        <MyOrganizationsTemplate
          data={organizationsWithDetails}
          publicPagesEnabled={publicPagesEnabled}
          onSearchChange={setSearchValue}
          searchValue={searchValue}
          showHeader={false}
          status={error ? "error" : isLoading ? "pending" : "success"}
        />
      </Stack>

      {isOrganizationDialogOpen && <CreateOrganizationDialog onClose={handleCloseOrganizationDialog} />}
    </Box>
  );
};
